import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { useMouseGlow } from './hooks/useMouseGlow';
import NodesBackground from './components/NodesBackground';
import CentralHome from './components/CentralHome';

const Admin = lazy(() => import('./components/Admin'));
const DeleteAccount = lazy(() => import('./components/DeleteAccount'));
const Support = lazy(() => import('./components/Support'));
const PrivacyPolicy = lazy(() => import('./components/PrivacyPolicy'));
const BlogsPage = lazy(() => import('./components/blog/BlogsPage'));
const BlogPost = lazy(() => import('./components/blog/BlogPost'));
const NotFound = lazy(() => import('./components/NotFound'));

function RouteLoader() {
  return (
    <div className="min-h-screen bg-base flex flex-col items-center justify-center font-mono text-[12px] tracking-[0.14em] text-slate-500 select-none">
      <div className="flex items-center gap-3">
        <span className="w-2.5 h-2.5 rounded-full bg-ember blink"></span>
        <span className="uppercase text-ink font-700">SYSTEM_LOADING...</span>
      </div>
      <div className="mt-2 text-[10px] text-faint">CONNECTING SECURE PROTOCOL</div>
    </div>
  );
}

function App() {
  useMouseGlow();

  useEffect(() => {
    console.log(
      `%c\n` +
      `███████╗███╗   ███╗██████╗ ███████╗██████╗  ██████╗ ███╗   ██╗\n` +
      `██╔════╝████╗ ████║██╔══██╗██╔════╝██╔══██╗██╔═══██╗████╗  ██║\n` +
      `█████╗  ██╔████╔██║██████╔╝█████╗  ██████╔╝██║   ██║██╔██╗ ██║\n` +
      `██╔══╝  ██║╚██╔╝██║██╔══██╗██╔══╝  ██╔══██╗██║   ██║██║╚██╗██║\n` +
      `███████╗██║ ╚═╝ ██║██████╔╝███████╗██║  ██║╚██████╔╝██║ ╚████║\n` +
      `╚══════╝╚═╝     ╚═╝╚═════╝ ╚══════╝╚═╝  ╚═╝ ╚═════╝ ╚═╝  ╚═══╝\n\n` +
      `[SYSTEM] Emberon Labs Protocol Terminal Active.\n` +
      `[INFO] OCPP compliance-readout console listening.\n` +
      `Type window.emberon.help() for available commands.`,
      "color: #39f59a; font-family: monospace; font-weight: bold;"
    );

    window.emberon = {
      help: () => {
        console.log("%cAvailable functions:\n - window.emberon.status()\n - window.emberon.ping()\n - window.emberon.compliance()", "color: #27d3ee; font-family: monospace;");
      },
      status: () => {
        console.log("%cSYSTEM STATUS: ACTIVE / VALID\nCONNECTORS: 1,284\nNETWORK UPTIME: 99.98%\nPOWER ORCHESTRATED: 8.4 GWh", "color: #39f59a; font-family: monospace;");
      },
      ping: () => {
        console.log("%cRequest -> HeartbeatRequest: {}\nResponse <- HeartbeatConf: { status: \"Nominal\", rtt: \"48ms\" }", "color: #e7efeb; font-family: monospace;");
      },
      compliance: () => {
        console.log("%cCompliant Protocols:\n- OCPP 1.6j\n- OCPP 2.0.1\n- IEC 61851\n- AES-256 / TLS 1.3", "color: #27d3ee; font-family: monospace;");
      }
    };
  }, []);

  return (
    <HelmetProvider>
      <Router>
        <NodesBackground />
        <div className="bg-grid"></div>
        <div className="bg-vign"></div>
        <Suspense fallback={<RouteLoader />}>
          <Routes>
            <Route path="/" element={<CentralHome />} />
            <Route path="/blogs" element={<BlogsPage />} />
            <Route path="/blog" element={<BlogsPage />} />
            <Route path="/blogs/:slug" element={<BlogPost />} />
            <Route path="/blog/:slug" element={<BlogPost />} />

            <Route path="/admin" element={
              <>
                <Helmet>
                  <title>Admin — Emberon Labs</title>
                  <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <Admin />
              </>
            } />
            <Route path="/delete-account" element={
              <>
                <Helmet>
                  <title>Account Deletion Request — Emberon Labs</title>
                  <meta name="description" content="Submit a request to permanently delete your account and erase associated personal information in compliance with privacy regulations." />
                  <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <DeleteAccount />
              </>
            } />
            <Route path="/support" element={
              <>
                <Helmet>
                  <title>Support — Emberon Labs</title>
                  <meta name="description" content="Get help with Emberon Labs apps and products. Submit a support ticket and our team will get back to you." />
                  <link rel="canonical" href="https://emberonlabs.in/support" />
                </Helmet>
                <Support />
              </>
            } />
            <Route path="/privacy-policy" element={
              <>
                <Helmet>
                  <title>Privacy Policy — Emberon Labs</title>
                  <meta name="description" content="Detailed privacy policy for Emberon Labs' mobile applications and website. Compliance disclosures for location data, Bluetooth permissions, and data erasure." />
                  <link rel="canonical" href="https://emberonlabs.in/privacy-policy" />
                </Helmet>
                <PrivacyPolicy />
              </>
            } />
            <Route path="*" element={
              <>
                <Helmet>
                  <title>404 Not Found — Emberon Labs</title>
                  <meta name="robots" content="noindex, nofollow" />
                </Helmet>
                <NotFound />
              </>
            } />
          </Routes>
        </Suspense>
      </Router>
    </HelmetProvider>
  );
}

export default App;
